import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { motion } from 'framer-motion';
import {
  Home, ChevronRight, BookMarked, Clock, Award,
  ArrowRight, GraduationCap, FlaskConical, HeartPulse,
  Shield, Activity
} from 'lucide-react';
import { fetchStudyPrograms } from '../utils/api';

const PROGRAMS = [
  {
    id: 'kesmas',
    name: 'Kesehatan Masyarakat',
    degree: 'S1',
    fullDegree: 'Sarjana Kesehatan Masyarakat (SKM)',
    accreditation: 'BAIK SEKALI',
    sks: 144,
    duration: '4 Tahun',
    students: 450,
    category: 'S1',
    icon: <Activity size={22} />,
    colorBadge: 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300',
    colorAccent: 'border-orange-400',
    colorTag: 'bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-300',
    description: 'Mencetak public health entrepreneur yang unggul dalam pemberdayaan masyarakat agroindustri, menghasilkan inovasi produk dan jasa kesehatan, serta berdaya saing global berbasis digital kesehatan.',
    highlights: ['Public Health Entrepreneurship', 'Pemberdayaan Agroindustri', 'Inovasi Kesehatan', 'Digital Kesehatan'],
    image: 'https://images.unsplash.com/photo-1659718282278-fd3ba02cb782?w=600&q=80',
  },
  {
    id: 'keperawatan',
    name: 'Keperawatan',
    degree: 'S1',
    fullDegree: 'Sarjana Keperawatan (S.Kep)',
    accreditation: 'BAIK SEKALI',
    sks: 148,
    duration: '4 Tahun',
    students: 680,
    category: 'S1',
    icon: <HeartPulse size={22} />,
    colorBadge: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300',
    colorAccent: 'border-emerald-400',
    colorTag: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-300',
    description: 'Mendidik calon perawat profesional yang kompeten dalam memberikan asuhan keperawatan holistik kepada individu, keluarga, dan masyarakat.',
    highlights: ['Keperawatan Klinik', 'Keperawatan Komunitas', 'Manajemen RS', 'Gawat Darurat'],
    image: 'https://images.unsplash.com/photo-1659353888906-adb3e0041693?w=600&q=80',
  },
  {
    id: 'farmasi',
    name: 'Farmasi',
    degree: 'S1',
    fullDegree: 'Sarjana Farmasi (S.Farm)',
    accreditation: 'BAIK SEKALI',
    sks: 144,
    duration: '4 Tahun',
    students: 520,
    category: 'S1',
    icon: <FlaskConical size={22} />,
    colorBadge: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300',
    colorAccent: 'border-blue-400',
    colorTag: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300',
    description: 'Menghasilkan sarjana farmasi yang unggul dalam sains dan inovasi kefarmasian berbasis pharmapreneurship dan kearifan lokal yang berdaya saing global.',
    highlights: ['Inovasi Sains', 'Pharmapreneurship', 'Kearifan Lokal', 'Daya Saing Global'],
    image: 'https://images.unsplash.com/photo-1766297248027-611186dbccc1?w=600&q=80',
  },
  {
    id: 'ners',
    name: 'Profesi Ners',
    degree: 'Profesi',
    fullDegree: 'Ners (Ns.)',
    accreditation: 'BAIK SEKALI',
    sks: 36,
    duration: '1 Tahun',
    students: 180,
    category: 'Profesi',
    icon: <Shield size={22} />,
    colorBadge: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
    colorAccent: 'border-red-400',
    colorTag: 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-300',
    description: 'Program profesi yang mempersiapkan mahasiswa keperawatan menjadi perawat profesional bersertifikat melalui praktik klinik intensif di berbagai wahana rumah sakit.',
    highlights: ['Praktik Klinik', 'ICU & Critical Care', 'Keperawatan Jiwa', 'Uji Kompetensi'],
    image: 'https://images.pexels.com/photos/35645528/pexels-photo-35645528.jpeg?w=600',
  },
  {
    id: 'apoteker',
    name: 'Profesi Apoteker',
    degree: 'Profesi',
    fullDegree: 'Apoteker (Apt.)',
    accreditation: 'B',
    sks: 36,
    duration: '1 Tahun',
    students: 120,
    category: 'Profesi',
    icon: <GraduationCap size={22} />,
    colorBadge: 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300',
    colorAccent: 'border-purple-400',
    colorTag: 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-300',
    description: 'Program profesi yang melatih mahasiswa farmasi menjadi apoteker bersertifikat yang kompeten dalam pelayanan kefarmasian klinis dan komunitas.',
    highlights: ['Apotek Komunitas', 'Farmasi RS', 'Regulasi BPOM', 'Uji Kompetensi'],
    image: 'https://images.pexels.com/photos/9628853/pexels-photo-9628853.jpeg?w=600',
    comingSoon: true,
  },
];

const FILTERS = ['Semua', 'S1', 'Profesi'];

const ICONS = { Activity, HeartPulse, FlaskConical, Shield, GraduationCap, BookMarked };
const THEME_CLASSES = {
  orange: {
    colorBadge: 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300',
    colorAccent: 'border-orange-400',
    colorTag: 'bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-300',
  },
  emerald: {
    colorBadge: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300',
    colorAccent: 'border-emerald-400',
    colorTag: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-300',
  },
  blue: {
    colorBadge: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300',
    colorAccent: 'border-blue-400',
    colorTag: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300',
  },
  red: {
    colorBadge: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
    colorAccent: 'border-red-400',
    colorTag: 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-300',
  },
  purple: {
    colorBadge: 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300',
    colorAccent: 'border-purple-400',
    colorTag: 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-300',
  },
};

const normalizeProgram = (program) => {
  const Icon = ICONS[program.icon] || BookMarked;
  const theme = THEME_CLASSES[program.theme] || THEME_CLASSES.purple;
  return {
    ...program,
    id: program.slug,
    name: program.cardName,
    fullDegree: program.degree,
    degree: program.degreeLabel,
    image: program.imageUrl,
    icon: <Icon size={22} />,
    ...theme,
  };
};

const imageVariant = (src, width) => {
  const supported = src?.includes('/api/public/program-studi-media/')
    || /\/program-studi\/[a-f0-9-]{36}\.webp(?:\?|$)/i.test(src);
  return supported ? `${src}${src.includes('?') ? '&' : '?'}w=${width}` : src;
};

function ProgramThumbnailSlider({ program }) {
  const sources = [
    program.image,
    ...(program.images || []).map((image) => image.imageUrl),
  ].filter((source, index, entries) => source && entries.indexOf(source) === index);
  const sourcesKey = sources.join('|');
  const duration = Math.min(30, Math.max(3, Number(program.slideDurationSeconds) || 5)) * 1000;
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updatePreference = () => setReducedMotion(media.matches);
    updatePreference();
    media.addEventListener?.('change', updatePreference);
    return () => media.removeEventListener?.('change', updatePreference);
  }, []);

  useEffect(() => {
    setActiveIndex(0);
  }, [sourcesKey]);

  useEffect(() => {
    if (sources.length < 2 || paused || reducedMotion) return undefined;
    const interval = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % sources.length);
    }, duration);
    return () => window.clearInterval(interval);
  }, [duration, paused, reducedMotion, sources.length, sourcesKey]);

  if (!sources.length) {
    return <div className="h-full w-full bg-gradient-to-br from-[#4C1D95] to-[#9333EA]" />;
  }

  return (
    <div
      className="absolute inset-0"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      {sources.map((source, index) => (
        <img
          key={source}
          src={imageVariant(source, 600)}
          alt={index === activeIndex ? program.name : ''}
          aria-hidden={index !== activeIndex}
          loading="lazy"
          className={`absolute inset-0 h-full w-full object-cover transition-[opacity,transform] duration-700 group-hover:scale-105 ${
            index === activeIndex ? 'opacity-100' : 'opacity-0'
          }`}
        />
      ))}
      {sources.length > 1 && (
        <div className="absolute bottom-3 left-1/2 z-20 flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-black/35 px-2 py-1.5 backdrop-blur-sm" aria-label={`${sources.length} gambar slider`}>
          {sources.map((source, index) => (
            <span key={source} className={`h-1.5 rounded-full transition-all duration-300 ${index === activeIndex ? 'w-4 bg-white' : 'w-1.5 bg-white/55'}`} />
          ))}
        </div>
      )}
    </div>
  );
}

const ProgramStudi = () => {
  const [activeFilter, setActiveFilter] = useState('Semua');
  const [programs, setPrograms] = useState(PROGRAMS);

  useEffect(() => {
    let active = true;
    fetchStudyPrograms()
      .then((records) => {
        if (active && records.length) setPrograms(records.map(normalizeProgram));
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const filtered = activeFilter === 'Semua'
    ? programs
    : programs.filter(p => p.category === activeFilter);
  const totalStudents = programs.reduce((total, program) => total + (Number(program.students) || 0), 0);

  return (
    <div data-testid="program-studi-page">
      <SEO
        title="Program Studi"
        description="Program studi unggulan STIKES Harapan Ibu Jambi: Keperawatan, Profesi Ners, Farmasi, dan Kesehatan Masyarakat. Terakreditasi dan berdaya saing."
      />
      <div className="page-hero pt-[70px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="flex items-center gap-2 text-white/70 text-sm mb-4">
              <Link to="/" className="hover:text-white transition-colors flex items-center gap-1">
                <Home size={14} /> Beranda
              </Link>
              <ChevronRight size={14} />
              <span className="text-white">Program Studi</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white font-heading mb-4">Program Studi</h1>
            <p className="text-white/80 max-w-xl text-base">
              Temukan program studi yang tepat untuk masa depan Anda bersama STIKES Harapan Ibu Jambi.
            </p>
          </motion.div>
        </div>
      </div>

      <div className="bg-[#F8F7FF] dark:bg-[#121020]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-4">
          <div className="flex items-center gap-2 flex-wrap">
            {FILTERS.map(f => (
              <button
                key={f}
                data-testid={`filter-${f.toLowerCase()}`}
                onClick={() => setActiveFilter(f)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  activeFilter === f
                    ? 'bg-[#7B2FBE] text-white shadow-lg shadow-purple-500/25'
                    : 'bg-white dark:bg-[#1A182E] text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:border-[#7B2FBE] hover:text-[#7B2FBE]'
                }`}
              >
                {f}
              </button>
            ))}
            <span className="ml-auto text-sm text-gray-500 dark:text-gray-400">{filtered.length} Program</span>
          </div>
        </div>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-20">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((prog, i) => (
              <motion.div
                key={prog.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                data-testid={`program-card-${prog.id}`}
                className={`bg-white dark:bg-[#1A182E] rounded-2xl overflow-hidden border-l-4 ${prog.colorAccent}
                  border border-gray-100 dark:border-gray-800 shadow-sm hover:shadow-xl transition-all duration-300 group flex flex-col`}
              >
                <Link to={`/program-studi/${prog.id}`} className="relative h-44 overflow-hidden block">
                  <ProgramThumbnailSlider program={prog} />
                  <div className="absolute inset-0 z-10 bg-gradient-to-t from-black/40 to-transparent" />
                  <span className={`absolute left-3 top-3 z-20 rounded-full px-2.5 py-0.5 text-xs font-bold ${prog.colorBadge}`}>
                    {prog.degree}
                  </span>
                  {prog.comingSoon && (
                    <span className="absolute right-3 top-3 z-20 rounded-full bg-yellow-400 px-2.5 py-0.5 text-[10px] font-bold text-yellow-900 shadow-sm animate-pulse">
                      COMING SOON
                    </span>
                  )}
                </Link>

                <div className="p-5 flex flex-col flex-1">
                  <div className="flex items-start gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${prog.colorBadge}`}>
                      {prog.icon}
                    </div>
                    <div>
                      <h3 className="font-bold text-[#1A1A2E] dark:text-white font-heading leading-tight">
                        <Link
                          to={`/program-studi/${prog.id}`}
                          data-testid={`title-link-${prog.id}`}
                          className="transition-colors hover:text-[#7B2FBE] dark:hover:text-[#A855F7] focus-visible:text-[#7B2FBE] focus-visible:outline-none focus-visible:underline underline-offset-4 hover:underline decoration-[#7B2FBE]/40"
                        >
                          {prog.name}
                        </Link>
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{prog.fullDegree}</p>
                    </div>
                  </div>

                  <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed mb-4 flex-1">{prog.description}</p>

                  <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mb-4 border-t border-gray-100 dark:border-gray-800 pt-3">
                    <span className="flex items-center gap-1"><BookMarked size={12} className="text-[#7B2FBE]" />{prog.sks} SKS</span>
                    <span className="flex items-center gap-1"><Clock size={12} className="text-[#7B2FBE]" />{prog.duration}</span>
                    <span className="flex items-center gap-1"><Award size={12} className="text-[#7B2FBE]" />Akred. {prog.accreditation}</span>
                  </div>

                  <div className="flex flex-wrap gap-1.5 mb-5">
                    {prog.highlights.map(h => (
                      <span key={h} className={`px-2 py-0.5 rounded-md text-xs font-medium ${prog.colorTag}`}>{h}</span>
                    ))}
                  </div>

                  <Link
                    to={`/program-studi/${prog.id}`}
                    data-testid={`view-detail-${prog.id}`}
                    className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl
                      bg-[#7B2FBE]/10 hover:bg-[#7B2FBE] text-[#7B2FBE] hover:text-white
                      font-medium text-sm transition-all duration-300 group/btn"
                  >
                    Lihat Detail
                    <ArrowRight size={15} className="group-hover/btn:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="bg-gradient-to-r from-[#4C1D95] via-[#7B2FBE] to-[#9333EA] py-12">
          <div className="max-w-5xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { val: String(programs.length), label: 'Program Studi' },
              { val: `${totalStudents.toLocaleString('id-ID')}+`, label: 'Mahasiswa Aktif' },
              { val: 'B', label: 'Akreditasi BAN-PT' },
              { val: '80+', label: 'Dosen & Tenaga Ahli' },
            ].map((s, i) => (
              <div key={i} className="text-white">
                <div className="text-3xl md:text-4xl font-bold font-heading">{s.val}</div>
                <div className="text-white/75 text-sm mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default ProgramStudi;
